import { FormArray, FormGroup } from '@angular/forms';
import { School } from '@models/school';
import { UserPartial } from '@models/user';
import { RoleType } from '@shared/enums/role-type';
import { FormHelpers } from '@shared/form-helpers';
import { primaryFormControlNames } from '@shared/manage-users/manage-users-form/constants/form-field-setup-data';
import _ from 'lodash';

export namespace CreateUserFormHelpers {
  export const omittableProperties: string[] = [
    'student',
    'parentFirstName',
    'parentLastName',
    'parentUserName',
  ];

  /**
   * Maps form data to UserPartials with updated information for users that have updates.
   *
   * Used to create users.
   *
   * @param managedUsersArrayAndRoleFormGroup Form group with New Users array and Role being managed
   * @param school School currently being managed
   * @param educationalUnitId  If provided, when creating a Parent user while managing a Student,
   * @param classroomId
   * @returns { UserPartial[] } Array of users being managed that have updates
   */
  // If only a single-user form, it'll always be an array of 1
  // Uses educationalUnitId when provided, else will use the educationalUnitId from provided school
  export function mapFormValuesToRequestData(
    managedUsersArrayAndRoleFormGroup: FormGroup, school: School,
    educationalUnitId: string = null, classroomId: string = null
  ): UserPartial[] {
    if (managedUsersArrayAndRoleFormGroup) {
      const managedUsersFormArray = managedUsersArrayAndRoleFormGroup
        .get(primaryFormControlNames.newUsers) as FormArray;

      const roleType = managedUsersArrayAndRoleFormGroup.get(primaryFormControlNames.roleType).value;
      const parentValues: UserPartial[] = [];
      /** Diabled Fields are omitted from {form}.value in Angular Forms
       * getRawValue() can be used if needed to get value of disabled field
       * (e.g. disabled password field's value of '*******' will not be returned in the .value map below)
       */
      let managedUsersFormArrayValue = managedUsersFormArray.value;
      if (roleType === RoleType.Teacher) {
        // For teachers, we need to getRawValue to include disabled existing teacher and new teacher values
        // added while creating a class from teacher login
        managedUsersFormArrayValue = managedUsersFormArray.getRawValue();
      }
      const userValues: Partial<UserPartial>[] = managedUsersFormArrayValue.map((values) => {
        const omitProperties = values.password
          ? [...omittableProperties]
          : ['password', 'confirmPassword', ...omittableProperties];
        const keptValues = _.omit<UserPartial>(values, omitProperties);

        if (roleType === RoleType.Student) {

          if (!keptValues.username.endsWith(`_${school?.abbreviatedSchoolId}`)) {
            keptValues.username = `${keptValues.username}_${school.abbreviatedSchoolId}`;
          }
          if (values.parentUserName) {
            parentValues.push({
              username: values.parentUserName,
              lastName: values.parentLastName,
              firstName: values.parentFirstName,
              userAssociationsToAdd: [{ associatedUserName: keptValues.username }],
              rolesToAdd: [{
                educationalUnitId: educationalUnitId ?? school.educationalUnitId,
                classroomId: classroomId ?? null,
                roleType: RoleType.Parent,
              }],
            });
          }
        }

        if (roleType === RoleType.Parent) {
          if (FormHelpers.isInvalidStudent(managedUsersFormArray, 'student')) {
            throw new Error('Student user not found');
          }
          keptValues.userAssociationsToAdd = [{
            associatedUserId: values.student?.userId,
            associatedUserName: `${values.student?.username}${values.student?.username.endsWith(`_${school.abbreviatedSchoolId}`) ? '' : `_${school.abbreviatedSchoolId}`}`,
          }];
        }

        return keptValues;
      });

      const uniqueParentValues: UserPartial[] = parentValues.reduce((ret, parent) => {
        const index = ret.findIndex(p => p.username === parent.username);
        if (index === -1) {
          return ret.concat(parent);
        }
        // Add the additional student association to the existing parent user to add.
        ret[index].userAssociationsToAdd.push({
          associatedUserName: parent.userAssociationsToAdd[0].associatedUserName,
        });
        return ret;
      }, []);

      return userValues
        .map(values => ({
          ...values,
          rolesToAdd: [
            {
              educationalUnitId: educationalUnitId ?? school.educationalUnitId,
              classroomId: classroomId ?? null,
              roleType,
            },
          ],
        } as UserPartial))
        .concat(uniqueParentValues);
    }

    return null;
  }
}
